import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Tag, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
import { BLOG_POSTS } from './BlogPage';
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find(p => p.slug === slug);
  // Redirect if post not found
  useEffect(() => {
    if (!post) {
      navigate('/blog');
    }
  }, [post, navigate]);
  useSEO({
    title: post ? post.title : 'Blog Post',
    description: post ? post.excerpt : 'Read this article on the Robeta Journal.'
  });
  if (!post) return null;
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-387">
      {/* 1. Header & Hero */}
      <article className="container mx-auto px-6 md:px-12 mb-24" data-id="element-388">
        <Breadcrumbs items={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Journal',
        href: '/blog'
      }, {
        label: post.title
      }]} className="mb-8" data-id="element-389" />

        <div className="max-w-4xl mx-auto text-center mb-12" data-id="element-390">
          <div className="flex items-center justify-center gap-4 mb-6 text-xs font-bold uppercase tracking-widest text-robeta-copper" data-id="element-391">
            <span className="flex items-center gap-2" data-id="element-392">
              <Tag className="w-3 h-3" data-id="element-393" /> {post.category}
            </span>
            <span className="w-1 h-1 rounded-full bg-robeta-black/20" data-id="element-394" />
            <span className="flex items-center gap-2 text-robeta-black/40" data-id="element-395">
              <Calendar className="w-3 h-3" data-id="element-396" /> {post.date}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-8" data-id="element-397">
            {post.title}
          </h1>

          <p className="text-xl font-light text-robeta-black/60 leading-relaxed max-w-2xl mx-auto" data-id="element-398">
            {post.excerpt}
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="w-full aspect-[21/9] overflow-hidden mb-16" data-id="element-399">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-400" />
        </motion.div>

        {/* 2. Content Body */}
        <div className="max-w-3xl mx-auto" data-id="element-401">
          <div className="prose prose-lg prose-headings:font-bold prose-headings:uppercase prose-headings:tracking-editorial prose-a:text-robeta-copper prose-img:rounded-sm text-robeta-black/70 font-light leading-relaxed mb-16" data-id="element-402">
            {post.content.map((block, index) => {
            switch (block.type) {
              case 'paragraph':
                return <p key={index} data-id="element-403">{block.text}</p>;
              case 'heading':
                return <h3 key={index} data-id="element-404">{block.text}</h3>;
              case 'blockquote':
                return <blockquote key={index} data-id="element-405">{block.text}</blockquote>;
              case 'list':
                return <ul key={index} data-id="element-406">
                      {block.items.map((item, i) => <li key={i} data-id="element-407">{item}</li>)}
                    </ul>;
              default:
                return null;
            }
          })}
          </div>

          {/* 3. Share & Tags */}
          <div className="border-t border-b border-robeta-black/10 py-8 mb-16 flex flex-col sm:flex-row justify-between items-center gap-6" data-id="element-408">
            <div className="flex items-center gap-4" data-id="element-409">
              <span className="text-xs font-bold uppercase tracking-widest text-robeta-black" data-id="element-410">
                Share:
              </span>
              <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-robeta-copper hover:text-white transition-colors" data-id="element-411">
                <Facebook className="w-4 h-4" data-id="element-412" />
              </button>
              <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-robeta-copper hover:text-white transition-colors" data-id="element-413">
                <Twitter className="w-4 h-4" data-id="element-414" />
              </button>
              <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-robeta-copper hover:text-white transition-colors" data-id="element-415">
                <Linkedin className="w-4 h-4" data-id="element-416" />
              </button>
            </div>
            <Link to="/blog" className="text-xs font-bold uppercase tracking-widest text-robeta-black hover:text-robeta-copper transition-colors flex items-center gap-2" data-id="element-417">
              <ArrowLeft className="w-4 h-4" data-id="element-418" /> Back to Journal
            </Link>
          </div>
        </div>
      </article>

      {/* 4. Related Posts */}
      <section className="bg-robeta-mist/10 py-24" data-id="element-419">
        <div className="container mx-auto px-6 md:px-12" data-id="element-420">
          <h2 className="text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-12 text-center" data-id="element-421">
            More Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-422">
            {BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 3).map(related => <Link key={related.id} to={`/blog/${related.slug}`} className="group block" data-id="element-423">
                  <div className="aspect-[3/2] overflow-hidden mb-4" data-id="element-424">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-425" />
                  </div>
                  <span className="text-xs font-bold text-robeta-copper uppercase tracking-widest block mb-2" data-id="element-426">
                    {related.category}
                  </span>
                  <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide group-hover:text-robeta-copper transition-colors" data-id="element-427">
                    {related.title}
                  </h3>
                </Link>)}
          </div>
        </div>
      </section>
    </main>;
}